import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, PLATFORM_ID, TransferState, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Utils } from '../utils';
import { VACANCY_RENDER_PAYLOAD, VACANCY_TRANSFER_KEY, VacancyResolution, interpretVacancyResolve } from '../vacancy-document';

/**
 * Classifies a vacancy URL. Server: only the renderer's request-scoped payload (a render never
 * makes its own API request). Browser: the transferred payload for the first page, then the API.
 */
@Injectable({ providedIn: 'root' })
export class VacancyDocumentService {
  private readonly http = inject(HttpClient);
  private readonly transferState = inject(TransferState);
  private readonly renderPayload = inject(VACANCY_RENDER_PAYLOAD, { optional: true });
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  public resolve(path: string): Observable<VacancyResolution> {
    const seeded = this.consumeRenderPayload(path);
    if (seeded) return of(seeded);
    if (!this.isBrowser) return of({ kind: 'unavailable', path });
    return this.http
      .get<unknown>(`${Utils.getApiUrl()}/vacancy-documents/resolve`, { params: { path }, observe: 'response' })
      .pipe(
        map((response) => interpretVacancyResolve(response.status, response.body, path)),
        catchError((error: HttpErrorResponse) => of(interpretVacancyResolve(error?.status ?? 0, error?.error, path))),
      );
  }

  private consumeRenderPayload(path: string): VacancyResolution | null {
    if (this.renderPayload && this.renderPayload.path === path) {
      this.transferState.set(VACANCY_TRANSFER_KEY, this.renderPayload);
      return this.renderPayload;
    }
    if (this.isBrowser && this.transferState.hasKey(VACANCY_TRANSFER_KEY)) {
      const transferred = this.transferState.get(VACANCY_TRANSFER_KEY, null);
      this.transferState.remove(VACANCY_TRANSFER_KEY);
      if (transferred?.path === path) return transferred;
    }
    return null;
  }
}
